// The ad-platform `begin_checkout` push, fired ONCE per Stripe Checkout
// session, right after the server opens it and before the tab leaves for
// Stripe — the browser half of the checkout conversion.
//
// Who counts it: GTM fans `begin_checkout` out to Google Ads ("Founding Dev
// Begin Checkout", a SECONDARY conversion action: observed, never a bidding
// goal), GA4 `begin_checkout`, and Meta `InitiateCheckout` with
// eventID = `checkout-<session>`. The server sends the same event to Meta's
// Conversions API with that event_id (routes/billing.js → sendCheckoutToAds),
// so Meta keeps one.
//
// Synchronous on purpose: the caller navigates to Stripe right after, so
// nothing here may be awaited. A double click reuses the idempotent session
// and the per-session localStorage flag keeps it to one push.

export interface CheckoutInfo {
  kind: 'plan' | 'topup';
  sessionId: string;
  value?: number | null;
  planId?: string | null;
}

export const checkoutFiredKey = (sessionId: string): string => `fd_checkout_fired:${sessionId}`;

export function trackBeginCheckout(info: CheckoutInfo | null | undefined): boolean {
  try {
    if (!info || !info.sessionId) return false;
    const key = checkoutFiredKey(info.sessionId);
    try {
      if (window.localStorage.getItem(key) === '1') return false;
      window.localStorage.setItem(key, '1');
    } catch {
      /* private mode: Meta still dedupes on the event id */
    }
    const value = Number(info.value) > 0 ? Number(Number(info.value).toFixed(2)) : undefined;
    const cta_source = info.kind === 'plan' ? `plan_${info.planId || 'unknown'}` : 'topup';
    (window.dataLayer = window.dataLayer || []).push({
      event: 'begin_checkout',
      event_id: `checkout-${info.sessionId}`,
      checkout_session_id: info.sessionId,
      value,
      currency: 'USD',
      kind: info.kind,
      plan_id: info.planId || undefined,
      eventProps: { cta_source, kind: info.kind, value },
    });
    return true;
  } catch {
    return false; /* analytics must never break checkout */
  }
}
